/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../base.component';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { DropdownModel } from '../../models';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { LogService, ModalService, UtilsService } from '../../services';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-csv-importer',
    templateUrl: './csv-importer.component.html',
    styleUrls: ['./csv-importer.component.scss']
})
export class CsvImporterComponent extends BaseComponent implements OnInit {
    /**
     * Get at the list item to scroll to them using native element.
     */
    @ViewChild('button', { static: true }) public button: any;

    /**
     * Get at the list item to scroll to them using native element.
     */
    @ViewChild('importField', { static: true }) public input: any;

    /**
     * Import button name
     */
    @Input() public buttonName: string = 'import';

    /**
     * A boolean to determine if the button is disabled.
     */
    @Input() public disabled: boolean = true;

    /**
     * Fields to match the form in the parent.
     */
    @Input() set formFields(formFields: any[]) {
        this.fields = formFields;
        this._formFields = formFields.map((item: any): any => item.value.toLowerCase());
    }

    /**
     * If one or more of the formFields relates to a key in parent, the objects should be passed in here.
     * Data should be formatted with key to match on and then id and name. (this doesn't check for duplicates in the data).
     * e.g. roles: [
     *     {
     *         id: 1,
     *         name: 'One'
     *     }
     * ]
     */
    @Input() set dataToMatch(data: any) {
        this._dataToMatch = this.utilsService.toLowerCaseObjectKeys(data);
    }

    /**
     * Show row numbers in the preview table.
     */
    @Input() public showRowsNumbers: boolean = false;

    /**
     * Output to the parent component.
     */
    @Output() public tableRowsOut: EventEmitter<any> = new EventEmitter();

    /**
     * Set the start text.
     */
    public startText: string = '- Select -';

    /**
     * CSV headers for the view.
     */
    public csvHeaders: string[] = [];

    /**
     * Number of lines in the CSV
     */
    public lineCount: number = 0;

    /**
     * Dropdown of form input for the parent component.
     */
    public dropdown: DropdownModel[] = [];

    /**
     * Store the used assignments
     */
    public hiddenContentKeys: any[] = [];

    /**
     * Form group.
     */
    public formGroup: any = new FormArray([]);

    /**
     * Formatted data to be submitted (is different from the table rows).
     */
    public formattedData: any[] = [];

    /**
     * Required fields Messaging.
     */
    public tableRequiredMessaging: any = {
        missingRequiredFields: '',
        totalSelectedRequiredFields: '',
    };

    /**
     * table headers formed by user selection.
     */
    public tableHeaders: string[] = [];

    /**
     * Table toggle - switch between results and errors
     */
    public viewResults: boolean = true;

    /**
     * Formatted table rows
     */
    public tableRows: any[] = [];

    /**
     * Error message
     */
    public errorMessage: string = '';

    /**
     * Errored table rows
     */
    public erroredRows: any[] = [];

    /**
     * Set error state to disable the submit button.
     */
    public hasErrors: boolean = false;

    /**
     * Auto preview cut off
     */
    public autoPreviewCutOff: number = 400;

    /**
     * CSV
     * @private
     */
    private csv: string = '';

    /**
     * Store the fields object.
     * @private
     */
    private fields: any[] = [];

    /**
     * Private variable for setter and getter.
     * @private
     */
    private _formFields: string [] = [];
    private _dataToMatch: any = {};

    constructor(
        private formBuilder: FormBuilder,
        private logService: LogService,
        private modalService: ModalService,
        private utilsService: UtilsService
    ) {
        super();

        this.modalService.closeParentModalEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    if (data.modalId === 'assign-csv-headers') {
                        this.resetComponent();
                    }
                }
            });
    }

    ngOnInit(): void {
        this.setUpFormGroup();
        this.setUpFormSubscribers();
        this.setUpDropdownContent();
    }

    get formFields(): string[] {
        return this._formFields;
    }

    get dataToMatch(): any {
        return this._dataToMatch;
    }

    /**
     * Force 'click' the file input to show the file browser
     * @param event
     */
    public openFileBrowser(event: any): void {
        this.setUpFormGroup();
        this.setUpFormSubscribers();

        event.preventDefault();
        if (event.target.className.includes('upload-button')) {
            event.target.nextSibling.click();
        }
    }

    /**
     * Handle file
     * Note: wrapped in a promise for testing purposes.
     * @param event
     */
    public onFileChange(event: any): Promise<string> {
        return new Promise<string>((resolve) => {
            this.logService.debug('onFileChange', 'start');
            if (event.target.files?.length) {
                const file: any = event.target.files[0];
                const fileExtension: string = '.' + this.utilsService.getExtensionFromFilename(file.name);
                if (fileExtension !== '.csv') {
                    this.logService.warn('onFileChange', 'start');
                } else {
                    const reader: FileReader = new FileReader();
                    reader.readAsText(file);
                    reader.onload = (): void => {
                        const csv: string = reader.result as string;
                        this.csvHeaders = this.getCsvHeaders(csv);
                        this.lineCount = this.getLineCount(csv);
                        this.csv = csv;
                        this.modalService.openModalInParent('assign-csv-headers');
                        if (this.lineCount) {
                            this.initPage();
                        }
                        resolve(csv);
                    };
                }
            }
        });
    }

    /**
     * Get rows.
     */
    public getRows(): any {
        return this.formGroup.get('rows').controls as FormArray;
    }

    /**
     * Toggle tables.
     */
    public toggleTables(): void {
        this.viewResults = !this.viewResults;
    }

    /**
     * Check in value is a number
     * @param value
     */
    public columnIsNumber(value: any): boolean {
        return typeof value === 'number';
    }

    /**
     * Check is value contain excluded items.
     * @param value
     */
    public valueIncludesExcludedItems(value: any): boolean {
        return value.includes('@') && value.includes(' and ');
    }

    /**
     * Cancel import
     */
    public cancel(): void {
        this.resetComponent();
        this.modalService.close('assign-csv-headers');
    }

    /**
     * Emit value to parent component
     */
    public submit(): void {
        this.modalService.close('assign-csv-headers');
        this.tableRowsOut.next(this.formattedData);
    }

    /**
     * Submit button is disabled.
     */
    public submitIsDisabled(): boolean {
        const parts: any = this.tableRequiredMessaging?.totalSelectedRequiredFields.split('/');

        return !(parts[0] === parts[1]) || this.hasErrors || !this.tableRows.length;
    }

    /**
     * Get headers.
     * @param csv
     * @private
     */
    private getCsvHeaders(csv: string): string[] {
        const allTextLines: string[] = csv.split(/\r\n|\n/);
        const headers: string[] = allTextLines[0].split(',');

        const cleanHeaders: any[] = [];
        headers.forEach((item: any, index: number): void => {
            cleanHeaders.push(item === '' ? `empty header ${index}` : item);
        });

        return cleanHeaders;
    }

    /**
     * Get line count.
     * @param csv
     * @private
     */
    private getLineCount(csv: string): number {
        const allTextLines: string[] = csv.split(/\r\n|\n/);
        const content: any[] = [];
        const headersLength: number = this.csvHeaders.length;

        for (let i = 1; i < allTextLines.length; i++) {
            const row: string = allTextLines[i];
            const cleanArray: any[] = this.getRowParts(row);

            if (cleanArray.length !== headersLength) {
                this.logService.error(
                    'CsvImporterComponent:getLineCount',
                    `The number of items in line ${i} doesn't match the number of headers: ` + cleanArray);
            } else {
                if (cleanArray.length) {
                    content.push(cleanArray);
                } else {
                    this.logService.debug('CsvImporterComponent:getLineCount', `The content of line ${i} is empty: ` + cleanArray);
                }
            }
        }

        return content.length;
    }

    /**
     * Set up the main form group.
     * @private
     */
    private setUpFormGroup(): void {
        this.formGroup = this.formBuilder.group({
            rows: this.formBuilder.array([])
        });
    }

    /**
     * Set up form subscribers
     * @private
     */
    private setUpFormSubscribers(): void {
        this.formGroup.get('rows').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    const selectedHeaders: any = data.map((item: any): any => item.field);
                    const cleanHeaders: any[] = selectedHeaders.filter((item: any): any => item !== this.startText);
                    const cleanArray: any[] = this.utilsService.cleanArray(cleanHeaders);
                    this.hiddenContentKeys = [...cleanArray];
                    // Auto display preview if line count is less than cut off .
                    if (this.lineCount < this.autoPreviewCutOff) {
                        this.preview();
                    }

                    this.setTableRequiredMessaging();
                }
            });
    }

    /**
     * Set the table required messaging.
     * @private
     */
    private setTableRequiredMessaging(): void {
        const requiredFields: any[] = this.fields.filter((item: any): any => item.required);
        const requiredFieldNames: string[] = requiredFields.map((item: any): string => item.value.toLowerCase());
        const missingRequiredFields: string[] = requiredFieldNames.filter(
            (item: string): boolean => !this.hiddenContentKeys.includes(item)
        );

        const notRequiredFields: any[] = this.fields.filter((item: any): any => !item.required);
        const notRequiredFieldNames: string[] = notRequiredFields.map((item: any): string => item.value.toLowerCase());
        const totalNonRequiredFields: string[] = this.hiddenContentKeys.filter(
            (item: string): boolean => notRequiredFieldNames.includes(item)
        );

        const totalSelectedRequiredFields: number = this.hiddenContentKeys.length - totalNonRequiredFields.length;

        this.tableRequiredMessaging = {
            missingRequiredFields: this.utilsService.replaceLastCommaWithAnd(missingRequiredFields?.toString(), true),
            totalSelectedRequiredFields: `${totalSelectedRequiredFields}/${requiredFields.length}`
        };
    }

    /**
     * Set up dropdown content.
     * @private
     */
    private setUpDropdownContent(): void {
        this.dropdown = this.utilsService.convertArrayToDropDown(this.formFields);
    }

    /**
     * Preview the table data.
     */
    public preview(): void {
        const data: any = this.formGroup.get('rows').value;
        const selectedHeaders: any[] = data.map((item: any): any => item.field);
        const cleanArray: any[] = this.utilsService.cleanArray(selectedHeaders);
        if (cleanArray?.length) {
            const processedData: any[] = this.processData();
            this.populateTableRows(processedData);
            this.formattedData = processedData;
        }
    }

    /**
     * Initialise page.
     * @private
     */
    private initPage(): void {
        const rows: FormArray = this.formGroup.get('rows') as FormArray;
        const dropdownKeys: string[] = this.dropdown.map(item => item.key);
        let index: number = 0;
        for (const header of this.csvHeaders) {
            rows.push(this.createFormControlRows(header, index, dropdownKeys));
            index++;
        }
    }

    /**
     * Create a form control for each header.
     * @param header
     * @param index
     * @param dropdownKeys
     * @private
     */
    private createFormControlRows(header: string, index: number, dropdownKeys: string[]): FormGroup {
        // Try and match header to dropdown key.
        const value: string = dropdownKeys.find(item => item.toLowerCase() === header?.toLowerCase());

        return this.formBuilder.group({
            index: [index],
            header: [header],
            field: [value || null],
        });
    }

    /**
     * Process table rows.
     * @private
     */
    private processData(): any[] {
        this.erroredRows = [];
        const allTextLines: string[] = this.csv.split(/\r\n|\n/);
        const formValues: any = this.formGroup.get('rows').value;
        const dataToMatchKeys: string[] = Object.keys(this.dataToMatch);
        allTextLines.shift();

        const dataRows: any[] = [];
        for (let i = 0; i < allTextLines.length; i++) {
            const row: string = allTextLines[i];
            const cleanArray: any[] = this.getRowParts(row);
            if (cleanArray.length && cleanArray.length === this.csvHeaders.length) {
                dataRows.push(this.formatRow(formValues, dataToMatchKeys, cleanArray));
            } else {
                this.erroredRows.push(cleanArray);
            }
        }

        if (this.erroredRows) {
            this.errorMessage = 'Mismatch with the headers and rows';
        }

        return dataRows;
    }

    /**
     * Get row parts.
     * @param row
     * @private
     */
    private getRowParts(row: string): any[] {
        const result: any[] = [];
        let current: string = '';
        let inQuotes: boolean = false;

        for (let i = 0; i < row.length; i++) {
            const rowPart: any = row[i];

            if (rowPart === '"') {
                inQuotes = !inQuotes;
            } else if (rowPart === ',' && !inQuotes) {
                result.push(current.trim());
                current = '';
            } else {
                current += rowPart;
            }
        }

        // Push the last value
        result.push(current.trim());

        return result.map(item => item.replace(/^"|"$/g, ''));
    }

    /**
     * Format row.
     * @param formValues
     * @param dataToMatchKeys
     * @param cleanArray
     * @private
     */
    private formatRow(formValues: any, dataToMatchKeys: string | any[], cleanArray: any[]): any {
        const newRowItem = {};
        for (const formValue of formValues) {
            if (formValue.field !== null) {
                const columnValue: string = cleanArray[formValue.index];
                if (columnValue !== '') {
                    const convertedValue: any = columnValue.includes(',')
                        ? this.convertCommaSeparatedValueToArray(columnValue)
                        : this.convertValueToType(columnValue);

                    newRowItem[formValue.field] = dataToMatchKeys.includes(formValue.field)
                        ? this.getKeyFromDataToMatch(convertedValue, formValue.field)
                        : convertedValue;
                } else {
                    newRowItem[formValue.field] = null;
                }
            }
        }

        return newRowItem;
    }

    /**
     * Get key from data to match.
     * @param convertedValue
     * @param field
     * @private
     */
    private getKeyFromDataToMatch(convertedValue: any, field: string): any {
        const matchedData: any = this.dataToMatch[field];
        if (Array.isArray(convertedValue)) {
            const values: any[] = [];
            for (const value of convertedValue) {
                values.push(this.getKey(value, matchedData));
            }

            return values;
        } else {
            return this.getKey(convertedValue, matchedData);
        }
    }

    /**
     * Get key.
     * @param convertedValue
     * @param matchedData
     * @private
     */
    private getKey(convertedValue: string, matchedData: any[]): any {
        const matchedIndex: number = matchedData.findIndex((item: any): boolean =>
            item.id === convertedValue ||
            (typeof convertedValue === 'string' && (item.name.toLowerCase() === convertedValue.toLowerCase()))
        );
        if (matchedIndex !== -1) {
            return matchedData[matchedIndex].id;
        } else {
            return convertedValue;
        }
    }

    /**
     * Populate table rows.
     * @param dataRows
     * @private
     */
    private populateTableRows(dataRows: any[]): void {
        this.hasErrors = false;
        const keys: string[] = Object.keys(dataRows[0]);
        this.tableHeaders = [...keys.filter(item => item !== this.startText)];
        const tableRows: any[] = [];
        const dataToMatchKeys: string[] = Object.keys(this.dataToMatch);

        for (const row of dataRows) {
            const rowsKeys: string[] = Object.keys(row);

            if (rowsKeys.filter(item => item !== this.startText)?.length) {
                tableRows.push(this.formatTableRows(dataToMatchKeys, rowsKeys, row));
            }
        }

        if (this.hasErrors) {
            this.logService.error('CsvImporterComponent:getMatched', 'Error matching one or more data keys');
        }

        this.tableRows = tableRows;
    }

    /**
     * Format table rows
     * @param dataToMatchKeys
     * @param rowsKeys
     * @param row
     * @private
     */
    private formatTableRows(dataToMatchKeys: string | any[], rowsKeys: string[], row: any): any {
        const newRowItem: any = {};
        for (const key of rowsKeys) {
            if (key !== this.startText && row[key] !== null) {
                newRowItem[key] = dataToMatchKeys.includes(key)
                    ? this.getMatched(row[key], this.dataToMatch[key], 'name')
                    : row[key];
            } else {
                newRowItem[key] = '';
            }
        }

        return newRowItem;
    }

    /**
     * Get matched item from list via lookup
     * @param lookupValue - array of Ids
     * @param list - list of object to lookup ids from
     * @param property - if supplied the returned list with be based on the property.
     * @private
     */
    private getMatched(lookupValue: any | any[], list: any[], property?: string): any {
        const lookupArray: any[] = (Array.isArray(lookupValue) ? lookupValue : [lookupValue]);

        const matchedRoles: any[] = [];
        if (lookupArray?.length && list?.length) {
            for (const lookupItem of lookupArray) {
                const match: any = list.find(item => this.findItem(item, lookupItem));
                if (match) {
                    matchedRoles.push(match[property] ? match[property] : match);
                }
            }
        }

        if (lookupArray?.length !== matchedRoles?.length) {
            this.hasErrors = true;

            return 'ERROR: Error matching one or more data keys on: ' + lookupValue.toString();
        }

        return property ? this.utilsService.replaceLastCommaWithAnd(matchedRoles.toString(), true) : matchedRoles;
    }

    /**
     * Find item.
     * @param item
     * @param lookupItem
     * @private
     */
    private findItem(item: any, lookupItem: string): boolean {
        const values: any[] = Object.values(item);
        const valuesAsStrings: any[] = values.map((item: string): any => item.toString().toLowerCase().trim());

        return valuesAsStrings.includes(lookupItem.toString().toLowerCase().trim());
    }

    /**
     * Convert comma separated value to array.
     * @param columnValue
     * @private
     */
    private convertCommaSeparatedValueToArray(columnValue: string): any {
        const array: string[] = columnValue.replace(/^"|"$/g, '').split(',');
        const typeCastArray: any[] = [];
        for (const item of array) {
            typeCastArray.push(this.convertValueToType(item));
        }

        return typeCastArray;
    }

    /**
     * Convert value to type.
     * @param columnValue
     * @private
     */
    private convertValueToType(columnValue: string): any {
        if (Number.isNaN(parseInt(columnValue, 10))) {
            return columnValue;
        } else {
            return parseInt(columnValue, 10);
        }
    }

    /**
     * Reset component.
     * @private
     */
    private resetComponent(): void {
        this.input.nativeElement.value = '';
        this.csvHeaders = [];
        this.lineCount = 0;
        this.hiddenContentKeys = [];
        this.tableRows = [];
        this.tableHeaders = [];
        this.hasErrors = false;
        this.csv = '';
    }
}
